export type PageDepartment =
  | 'home'
  | 'user'
  | 'commercial'
  | 'marketing'
  | 'operations'
  | 'merch-product'
  | 'executive'
  | 'finance'
  | 'legal'
  | 'transformation'
  | 'external';

export type PageIntegrationRequirement =
  | 'gorgias'
  | 'shopify'
  | 'cloudinary'
  | 'gtm'
  | 'bigquery'
  | 'klaviyo'
  | 'kodif'
  | 'sage'
  | 'flip';

export interface PageRegistryItem {
  tab: string;
  label: string;
  department: PageDepartment;
  path: string;
  legacyPaths?: string[];
  requiresIntegration?: PageIntegrationRequirement;
}

export const PAGE_REGISTRY = {
  dashboard: { tab: 'dashboard', label: 'Dashboard', department: 'home', path: '/the-mill' },
  fulfillment: {
    tab: 'fulfillment',
    label: 'Fulfillment',
    department: 'operations',
    path: '/operations/fulfillment/dashboard',
    legacyPaths: ['/fulfillment'],
  },
  'operations-inventory': {
    tab: 'operations-inventory',
    label: 'Inventory',
    department: 'operations',
    path: '/operations/inventory',
  },
  'operations-oos-queue': {
    tab: 'operations-oos-queue',
    label: 'OOS Queue',
    department: 'operations',
    path: '/operations/oos-queue',
  },
  'split-ship': {
    tab: 'split-ship',
    label: 'Split Ship',
    department: 'operations',
    path: '/operations/split-ship',
  },
  'operations-cancellations': {
    tab: 'operations-cancellations',
    label: 'Cancellations',
    department: 'operations',
    path: '/operations/cancellations',
  },
  'target-tna': {
    tab: 'target-tna',
    label: 'TNA',
    department: 'merch-product',
    path: '/product/target-tna',
  },
  'production-wip': {
    tab: 'production-wip',
    label: 'Production WIP',
    department: 'merch-product',
    path: '/product/production-wip',
    legacyPaths: ['/product/sage-data'],
    requiresIntegration: 'sage',
  },
  'exec-pulse': {
    tab: 'exec-pulse',
    label: 'Exec Pulse',
    department: 'executive',
    path: '/leadership/exec-pulse',
  },
  'daily-ritual': {
    tab: 'daily-ritual',
    label: 'Daily',
    department: 'user',
    path: '/user/daily-ritual',
    legacyPaths: ['/leadership/daily-ritual'],
  },
  'exec-dashboards': {
    tab: 'exec-dashboards',
    label: 'Dashboards',
    department: 'executive',
    path: '/leadership/dashboards',
  },
  'team-priorities': {
    tab: 'team-priorities',
    label: 'Team Priorities',
    department: 'executive',
    path: '/leadership/team-priorities',
  },
  boards: {
    tab: 'boards',
    label: 'Boards',
    department: 'user',
    path: '/user/boards',
    legacyPaths: ['/leadership/boards'],
  },
  approvals: {
    tab: 'approvals',
    label: 'Approvals',
    department: 'user',
    path: '/user/approvals',
    legacyPaths: ['/approvals'],
  },
  coaching: {
    tab: 'coaching',
    label: 'Coaching',
    department: 'executive',
    path: '/leadership/coaching',
  },
  learning: {
    tab: 'learning',
    label: 'Learning',
    department: 'user',
    path: '/user/learning',
    legacyPaths: ['/leadership/learning', '/learning'],
  },
  'tech-stack': {
    tab: 'tech-stack',
    label: 'Tech Stack',
    department: 'executive',
    path: '/leadership/tech-stack',
  },
  'knowledge-base': {
    tab: 'knowledge-base',
    label: 'Knowledge Base',
    department: 'executive',
    path: '/leadership/knowledge-base',
  },
  'netsuite-master-plan': {
    tab: 'netsuite-master-plan',
    label: 'NS Master Plan',
    department: 'executive',
    path: '/leadership/netsuite-master-plan',
  },
  github: {
    tab: 'github',
    label: 'GitHub',
    department: 'transformation',
    path: '/engineering/github',
    legacyPaths: ['/github'],
  },
  agents: {
    tab: 'agents',
    label: 'Agents',
    department: 'transformation',
    path: '/engineering/agents',
  },
  skills: {
    tab: 'skills',
    label: 'Skills',
    department: 'transformation',
    path: '/engineering/skills',
  },
  'mill-deployments': {
    tab: 'mill-deployments',
    label: 'Deployments',
    department: 'transformation',
    path: '/engineering/deployments',
  },
  'ai-os': {
    tab: 'ai-os',
    label: 'AI OS',
    department: 'transformation',
    path: '/engineering/ai-os',
  },
  studio: {
    tab: 'studio',
    label: 'Studio',
    department: 'marketing',
    path: '/studio',
    legacyPaths: ['/media-studio', '/image-studio', '/video-studio'],
  },
  email: {
    tab: 'email',
    label: 'Klaviyo',
    department: 'marketing',
    path: '/klaviyo',
    legacyPaths: ['/email', '/email-studio'],
    requiresIntegration: 'klaviyo',
  },
  'notion-ai-tasks': {
    tab: 'notion-ai-tasks',
    label: 'Notion AI Tasks',
    department: 'transformation',
    path: '/engineering/notion-ai-tasks',
  },
  'transformation-dashboard': {
    tab: 'transformation-dashboard',
    label: 'AI Command Centre',
    department: 'transformation',
    path: '/transformation',
  },
} as const satisfies Record<string, PageRegistryItem>;

export type RegisteredTabId = keyof typeof PAGE_REGISTRY;

export const LEADERSHIP_TAB_IDS = Object.values(PAGE_REGISTRY)
  .filter((page) => page.department === 'executive')
  .map((page) => page.tab) as Array<Extract<RegisteredTabId, string>>;

export const USER_TAB_IDS = Object.values(PAGE_REGISTRY)
  .filter((page) => page.department === 'user')
  .map((page) => page.tab) as Array<Extract<RegisteredTabId, string>>;

export function isRegisteredTab(tab: string): tab is RegisteredTabId {
  return Object.prototype.hasOwnProperty.call(PAGE_REGISTRY, tab);
}

export function pagePath(tab: string): string | null {
  return isRegisteredTab(tab) ? PAGE_REGISTRY[tab].path : null;
}

export function isDepartmentTab(tab: string, department: PageDepartment): boolean {
  return isRegisteredTab(tab) && PAGE_REGISTRY[tab].department === department;
}

export function tabForPath(path: string): string | null {
  for (const page of Object.values(PAGE_REGISTRY) as PageRegistryItem[]) {
    if (path === page.path || path.startsWith(`${page.path}/`)) return page.tab;
    if (page.legacyPaths?.some((legacyPath) => path === legacyPath || path.startsWith(`${legacyPath}/`))) {
      return page.tab;
    }
  }
  return null;
}
